import { getDataClient } from "@/lib/dataClient";
import { useDataQuery } from "@/hooks/useDataQuery";

export interface AiUsageLog {
  id: string;
  user: string;
  model: string;
  taskType: string;
  tokens: number;
  durationMs: number;
  status: string;
  time: string;
}

export interface AiModelUsage {
  model: string;
  calls: number;
  tokens: number;
  percent: number;
}

export interface AiTodayStats {
  totalCalls: number;
  activeUsers: number;
  avgLatencyMs: number;
  successRate: number;
  totalTokens: number;
  modelUsage: AiModelUsage[];
}

// Yerel saate göre bugünün başlangıcını ISO olarak döndürür.
function localTodayStartIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

interface LogRow {
  id: string;
  user_id: string | null;
  model: string | null;
  task_type: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  duration_ms: number | null;
  status: string | null;
  created_at: string;
}

interface ProfileRow {
  user_id: string | null;
  email: string | null;
  full_name: string | null;
}

/**
 * Son AI çağrı loglarını çeker ve kullanıcı adını (profiles üzerinden) iliştirir.
 */
export function useAiUsageLogs(limit = 100) {
  return useDataQuery<AiUsageLog[]>(async () => {
    const dataClient = getDataClient();

    const { data: logs, error } = await dataClient
      .from("ai_usage_logs")
      .select("id, user_id, model, task_type, input_tokens, output_tokens, duration_ms, status, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw new Error(error.message);

    const rows = (logs ?? []) as LogRow[];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter((id): id is string => !!id)));

    let profileMap = new Map<string, ProfileRow>();
    if (userIds.length > 0) {
      const { data: profiles, error: pErr } = await dataClient
        .from("profiles")
        .select("user_id, email, full_name")
        .in("user_id", userIds);
      if (!pErr) {
        (profiles ?? []).forEach((p: ProfileRow) => {
          if (p.user_id) profileMap.set(p.user_id, p);
        });
      }
    }

    return rows.map((r) => {
      const prof = r.user_id ? profileMap.get(r.user_id) : undefined;
      const name = prof?.full_name ?? prof?.email ?? (r.user_id ? r.user_id.slice(0, 8) : "—");
      return {
        id: r.id,
        user: name,
        model: r.model ?? "—",
        taskType: r.task_type ?? "—",
        tokens: (r.input_tokens ?? 0) + (r.output_tokens ?? 0),
        durationMs: r.duration_ms ?? 0,
        status: r.status ?? "success",
        time: shortTime(r.created_at),
      };
    });
  }, [limit]);
}

/**
 * Bugünkü AI kullanım istatistiklerini hesaplar (çağrı, kullanıcı, gecikme, başarı, token).
 */
export function useAiTodayStats() {
  return useDataQuery<AiTodayStats>(async () => {
    const dataClient = getDataClient();

    const { data: logs, error } = await dataClient
      .from("ai_usage_logs")
      .select("user_id, model, input_tokens, output_tokens, duration_ms, status")
      .gte("created_at", localTodayStartIso());

    if (error) throw new Error(error.message);

    const rows = (logs ?? []) as {
      user_id: string | null;
      model: string | null;
      input_tokens: number | null;
      output_tokens: number | null;
      duration_ms: number | null;
      status: string | null;
    }[];

    const totalCalls = rows.length;
    const activeUsers = new Set(rows.map((r) => r.user_id).filter((id): id is string => !!id)).size;

    const latencies = rows
      .map((r) => r.duration_ms)
      .filter((v): v is number => typeof v === "number" && v > 0);
    const avgLatencyMs =
      latencies.length > 0
        ? Math.round(latencies.reduce((s, v) => s + v, 0) / latencies.length)
        : 0;

    const successCount = rows.filter((r) => !r.status || r.status === "success").length;
    const successRate = totalCalls > 0 ? Math.round((successCount / totalCalls) * 1000) / 10 : 100;

    const totalTokens = rows.reduce(
      (s, r) => s + (r.input_tokens ?? 0) + (r.output_tokens ?? 0),
      0,
    );

    const modelMap = new Map<string, { calls: number; tokens: number }>();
    rows.forEach((r) => {
      const key = r.model ?? "Bilinmeyen";
      const cur = modelMap.get(key) ?? { calls: 0, tokens: 0 };
      cur.calls += 1;
      cur.tokens += (r.input_tokens ?? 0) + (r.output_tokens ?? 0);
      modelMap.set(key, cur);
    });

    const modelUsage: AiModelUsage[] = Array.from(modelMap.entries())
      .map(([model, v]) => ({
        model,
        calls: v.calls,
        tokens: v.tokens,
        percent: totalCalls > 0 ? Math.round((v.calls / totalCalls) * 100) : 0,
      }))
      .sort((a, b) => b.calls - a.calls);

    return {
      totalCalls,
      activeUsers,
      avgLatencyMs,
      successRate,
      totalTokens,
      modelUsage,
    };
  });
}